/** A bundle, configured as a tree beside one member's whole configuration (`CAT-022`). */

import { useState } from "react";
import type { BundleResolution } from "@nebulize/sdk";

import { Banner, Button, Stack, StatusBadge, Toolbar } from "@/components/ui";

import { useBundleResolution } from "../../activities/lib/queries";
import { useCatalog } from "../../spaces/lib/queries";
import { Chip } from "../../components/Chips";
import { Dot, Mono, Muted } from "../../components/Text";
import { isLive, runStatusOf } from "../../lib/runState";
import type { SpaceActivities } from "../../spaces/lib/queries";
import type { AddedActivityRow, ProposedBinding, SpaceRow } from "../../types";

import { missingMembers, useMaterialise } from "../lib/materialise";
import { readinessOf, blocks } from "../lib/readiness";
import { ActivityPanel } from "../ActivityPanel";

import { Loaded, Nothing, Section } from "./Section";

/** The sdk exports the resolution and not its member shape. */
type ResolvedMember = BundleResolution["members"][number];

/** One row of the tree: a member, the added row serving it here, and how deep it sits. */
interface MemberRow {
  member: ResolvedMember;
  /** `CAT-022` landed: an older bundle's unnamed member takes its activity id as its name. */
  name: string;
  depth: number;
  added: AddedActivityRow | null;
}

function treeRows(
  members: readonly ResolvedMember[],
  activities: SpaceActivities,
): MemberRow[] {
  return members.map((member) => ({
    member,
    name: member.name ?? member.activity_id,
    depth: member.depth,
    added: activities.rows.find((one) => one.activity_id === member.activity_id) ?? null,
  }));
}

function MemberButton({
  row,
  chosen,
  activities,
  space,
  onPick,
}: {
  readonly row: MemberRow;
  readonly chosen: boolean;
  readonly activities: SpaceActivities;
  readonly space: SpaceRow;
  readonly onPick: () => void;
}) {
  const live = row.added !== null && isLive(runStatusOf(row.added.mount));
  const readiness =
    row.added === null ? null : readinessOf(row.added, activities, space.connections);
  const unready = readiness !== null && blocks(readiness.state);

  return (
    <button
      type="button"
      className={chosen ? "cx-member cx-member-on" : "cx-member"}
      style={{ paddingLeft: `${String(8 + row.depth * 14)}px` }}
      aria-pressed={chosen}
      onClick={onPick}
    >
      <span className="cx-member-dots">
        <Dot tone={live ? "live" : "off"} />
        <Dot tone={row.added === null ? "off" : unready ? "warn" : "live"} />
      </span>
      <span className="cx-member-body">
        <span className="cx-node-name">{row.name}</span>
        <Muted small>
          <Mono>
            {`${row.member.activity_id} ${row.member.version ?? ""} via ${row.member.path.join(" / ")}`}
          </Mono>
        </Muted>
      </span>
      {row.added === null ? <StatusBadge tone="neutral">not added</StatusBadge> : null}
    </button>
  );
}

export interface CompositionPanelProps {
  readonly root: AddedActivityRow;
  readonly space: SpaceRow;
  readonly activities: SpaceActivities;
  /** Re-subjects the whole tray onto one member, so every ordinary panel points at it. */
  readonly onSelect: (addedActivityId: string) => void;
  readonly onClear: () => void;
}

export function CompositionPanel({
  root,
  space,
  activities,
  onSelect,
  onClear,
}: CompositionPanelProps) {
  const spaceId = space.space.space_id;
  const entry = activities.entryFor(root);
  const resolution = useBundleResolution(root.activity_id, true);
  const [picked, setPicked] = useState<string>(root.activity_id);
  const materialise = useMaterialise();
  const catalog = useCatalog();

  const manifest = entry?.manifest;
  const missing = manifest === undefined ? [] : missingMembers(manifest, activities.rows);
  const newest = new Map((catalog.data ?? []).map((one) => [one.activity_id, one.version]));
  const proposed: ProposedBinding[] = entry?.manifest.bundle?.proposed_bindings ?? [];
  const rows = treeRows(resolution.data?.members ?? [], activities);
  const chosen =
    picked === root.activity_id ? null : (rows.find((one) => one.member.activity_id === picked) ?? null);
  const subject = chosen?.added ?? root;

  return (
    <div className="cx-compose">
      <div className="cx-compose-tree">
        <Section title="Members">
          <Stack gap={2}>
            <button
              type="button"
              className={picked === root.activity_id ? "cx-member cx-member-on" : "cx-member"}
              aria-pressed={picked === root.activity_id}
              onClick={() => {
                setPicked(root.activity_id);
              }}
            >
              <span className="cx-member-body">
                <span className="cx-node-name">{root.name}</span>
                <Muted small>
                  <Mono>{`${root.activity_id} ${root.activity_version}`}</Mono>
                </Muted>
              </span>
              <StatusBadge tone="info">root</StatusBadge>
            </button>

            <Loaded query={resolution} rows={3}>
              {() =>
                rows.length === 0 ? (
                  <Nothing headline="No member" />
                ) : (
                  <>
                    {rows.map((one) => (
                      <MemberButton
                        key={one.member.activity_id}
                        row={one}
                        chosen={picked === one.member.activity_id}
                        activities={activities}
                        space={space}
                        onPick={() => {
                          setPicked(one.member.activity_id);
                        }}
                      />
                    ))}
                  </>
                )
              }
            </Loaded>

            {manifest === undefined ? null : (
              <Stack gap={2}>
                <Button
                  size="sm"
                  disabled={materialise.isPending || (missing.length === 0 && proposed.length === 0)}
                  onClick={() => {
                    materialise.mutate({
                      spaceId,
                      manifest,
                      rows: activities.rows,
                      newest,
                    });
                  }}
                >
                  {materialise.isPending
                    ? "Adding"
                    : missing.length === 0
                      ? "Wire what it proposes"
                      : `Add ${String(missing.length)} missing and wire`}
                </Button>
                {materialise.data === undefined ? null : (
                  <Banner tone={materialise.data.refusals.length === 0 ? "success" : "warning"}>
                    <div>
                      <div>
                        {`${String(materialise.data.added)} added, ${String(materialise.data.wired)} wired, ${String(materialise.data.arranged)} placed`}
                      </div>
                      {materialise.data.refusals.map((one) => (
                        <div key={one}>{one}</div>
                      ))}
                    </div>
                  </Banner>
                )}
                {materialise.isError ? (
                  <Banner tone="danger">{materialise.error.message}</Banner>
                ) : null}
              </Stack>
            )}
          </Stack>
        </Section>

        <Section title="Proposed wires">
          {proposed.length === 0 ? (
            <Muted small>none proposed</Muted>
          ) : (
            <Stack gap={2}>
              {proposed.map((one, index) => (
                <div className="cx-row" key={`${one.source}${one.export}${String(index)}`}>
                  <div className="cx-row-id">
                    <div>
                      <div className="cx-node-name">{`${one.source} -> ${one.target}`}</div>
                      <Muted small>
                        <Mono>{`${one.export} -> ${one.trigger}`}</Mono>
                      </Muted>
                    </div>
                  </div>
                  {one.enabled_by_default === false ? (
                    <StatusBadge tone="neutral">off by default</StatusBadge>
                  ) : (
                    <StatusBadge tone="success">on by default</StatusBadge>
                  )}
                </div>
              ))}
            </Stack>
          )}
        </Section>

        <Section title="Removing the bundle">
          <div className="cx-chips">
            {rows.map((one) => (
              <Chip key={one.member.activity_id} tone={one.member.removed_with_root ? undefined : "ns"}>
                {`${one.name}${one.member.removed_with_root ? "" : ", stays"}`}
              </Chip>
            ))}
          </div>
        </Section>
      </div>

      <div className="cx-compose-pane">
        {chosen !== null && chosen.added === null ? (
          <Nothing headline={`${chosen.name} is not added to this space`} />
        ) : (
          <>
            {chosen?.added == null ? null : (
              <Toolbar>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    onSelect(subject.added_activity_id);
                  }}
                >
                  {`Point every panel at ${chosen.name}`}
                </Button>
              </Toolbar>
            )}
            <ActivityPanel
              added={subject}
              activities={activities}
              spaceId={spaceId}
              onClear={onClear}
            />
          </>
        )}
      </div>
    </div>
  );
}
